import { Dispatch, SetStateAction, useContext } from "react";
import styles from "./CartFormCards.module.css";
import { Action, ProductObj, VariantsObj } from "../../../Bundles/Types";
import { CartConfigInterface, custom_field } from "../../CartTypes";
import {
    Banner,
    Button,
    Checkbox,
    Divider,
    FormLayout,
    LegacyCard,
    RadioButton,
    RangeSlider,
    Select,
    TextField,
} from "@shopify/polaris";
import TextEditorCart from "../../../../components/TextEditorCart/TextEditorCart";
import { asChoiceField, Field } from "@shopify/react-form";
import MediaGallery from "../../../../components/MediaGallery/MediaGallery";
import NewCustomColorPicker from "../../../../components/NewCustomColorPicker/NewCustomColorPicker";
import { AvailableCartDrawerTemplates, fetchProductsByIds, FontStyles } from "../../../Bundles/constants";
import { AppConfigContext } from "../../../../context/AppConfigContext";
import { getNumberFieldSuffix } from "../../../../utilities/constants";
import CloseIcon from "../../CartIcons/CloseIcon";
import { FieldRow, StepsFieldSection } from "../../../Bundles/BundleBuilder/BundleBuilderContentForm/BBStep";
import ShopifyNativeError from "../../../../components/ShopifyNativeError/ShopifyNativeError";
import { FormProvider, useForm } from "react-hook-form";

const ProductRow = ({
    product,
    idx,
    updateKey,
    dispatchCartConfig,
    products,
}: {
    product: ProductObj;
    idx: number;
    updateKey: string;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
    products: Field<ProductObj[]>;
}) => {
    const removeProduct = () => {
        const updatedProducts = [...products.value];
        updatedProducts.splice(idx, 1);
        products.onChange(updatedProducts);
        dispatchCartConfig({
            isComplex: true,
            payload: {
                [updateKey]: updatedProducts,
            },
            key: "additional_product",
        });
    };

    return (
        <div
            key={product?.variants[0]?.id + idx}
            style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                borderRadius: 10,
                backgroundColor: "white",
                padding: "5px",
            }}
        >
            <div className={styles.productMeta}>
                <div className={styles.productName}>
                    <div className={styles.productImageWrapper}>
                        <img alt={product?.images[0]?.altText} src={product?.images[0]?.originalSrc} />
                    </div>
                    <div className={styles.productTitleWrapper}>
                        <div
                            style={{
                                whiteSpace: "normal",
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                            onClick={() => {
                                if (shopify.config.shop) {
                                    window.open(`https://${shopify.config.shop}/products/${product?.handle}`, "_blank");
                                }
                            }}
                            className={styles.productTitle}
                        >
                            {product?.title}
                        </div>
                    </div>
                </div>
                <div
                    onClick={removeProduct}
                    style={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginRight: 10,
                    }}
                >
                    <CloseIcon />
                </div>
            </div>
        </div>
    );
};

const CartAdditionalProductForm = ({
    fields,
    dispatchCartConfig,
    showCartPreview,
    sft,
}: {
    submit: any;
    fields: any;
    showCartPreview: boolean;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
    sft: string;
}) => {
    const { money_format } = useContext(AppConfigContext);
    const methods = useForm();
    const {
        selected_drawer_template,
        additional_product: {
            product_type,
            shipping_protection_title,
            shipping_protection_subtext,
            shipping_protection_price,
            shipping_protection_images,
            additional_product_shipping_gift_checkbox,
            additional_product_addon_checkbox,
            additional_addon_products,
            custom_fields_checkbox,
            custom_fields,
            // shipping_protection_incremental_value,
            // shipping_protection_price_type,
            additional_addon_title,
            additional_addon_subtext,
            addon_modal_title,
            addon_selector_label,

            gift_wrapping_title,
            gift_wrapping_subtext,
            gift_wrapping_price,
            gift_wrapping_images,
            // gift_wrapping_incremental_value,
            // gift_wrapping_price_type,
        },
        additional_product_design: {
            additional_product_card_bg,
            additional_product_title_color,
            additional_product_description_color,
            additional_product_border_color,
            additional_product_border_gradient_status,
            additional_product_border_gradient_direction,
            additional_product_border_gradient_color,
            additional_product_title_size,
            additional_product_description_size,
            addon_modal_title_size,
            additional_product_description_style,

            addon_modal_background_color,
            addon_modal_title_color,

            addon_modal_primary_button_bg,
            addon_modal_primary_button_text_color,
            addon_modal_secondary_button_text_color,
        },
    } = fields;

    const handleProductSearch = async (key: "additional_addon_products", _preSelected: ProductObj[] = []) => {
        const preSelected = _preSelected?.map((product: ProductObj) => ({
            id: product?.id,
            variants: product?.variants.map((variant: any) => ({
                id: variant?.id,
            })),
        }));

        const selected = await shopify.resourcePicker({
            type: "product",
            multiple: true,
            query: "",
            action: "select",
            selectionIds: preSelected,
            filter: {
                query: "status:active AND published_status:published",
                variants: true,
            },
        });

        if (selected) {
            const productsArr: ProductObj[] = [];
            selected?.flatMap((product: any) => {
                productsArr.push({
                    id: product?.id,
                    images: product?.images,
                    title: product?.title,
                    handle: product?.handle,
                    quantity: 1,
                    maxProductStock: product?.totalInventory,
                    selectedVariants: [],
                    subscriptions: [],
                    oneTimePurchase: true,
                    isSubscribed: false,
                    variants: product?.variants?.map((variant: VariantsObj) => ({
                        image: { ...variant.image },
                        id: variant?.id,
                        displayName: variant.displayName,
                        title: variant?.title?.toLowerCase() == "default title" ? product?.title : variant?.title,
                        price: variant?.price,
                        quantity: 1,
                        selectedOptions: variant?.selectedOptions,
                        copyPrice: variant.price,
                        compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice : variant.price,
                    })),
                    options: product?.options,
                });
            });

            const productIds = productsArr.map((p) => p.id);
            const updatedProductsArr = await fetchProductsByIds(productIds, false, sft);

            productsArr.forEach((p) => {
                const fresh = updatedProductsArr.find((up: any) => up.id === p.id);
                if (!fresh) return;

                p.variants.forEach((variant) => {
                    const freshVariant = fresh.variants.nodes.find((fv: any) => fv.id === variant.id);
                    if (!freshVariant) return;

                    variant.price = freshVariant.price.amount;
                    variant.compareAtPrice = freshVariant.compareAtPrice?.amount ?? freshVariant.price.amount;

                    if (freshVariant.image) {
                        variant.image = { src: freshVariant.image.url };
                    }
                });
            });

            additional_addon_products.onChange(productsArr);
            dispatchCartConfig({
                isComplex: true,
                payload: {
                    [key]: productsArr,
                },
                key: "additional_product",
            });
        }
    };

    const handleAddFields = (pIdx = 0, action: "add" | "remove", idx?: number) => {
        let updatedFields: custom_field[] = [...custom_fields.value];

        if (action === "add") {
            const nextIndex = updatedFields.length + 1;
            updatedFields.push({
                field_type: "textarea",
                field_title: `Custom Field ${nextIndex}`,
                field_placeholder: "Enter text",
                is_required: false,
                options: [],
            });
        } else if (action === "remove" && idx !== undefined) {
            updatedFields = updatedFields.filter((_, id) => id !== idx);
        }

        custom_fields.onChange(updatedFields);
        dispatchCartConfig({
            isComplex: true,
            key: "additional_product",
            payload: {
                custom_fields: updatedFields,
            },
        });
    };

    const handleEditFields = (pIdx = 0, idx: number, subKey: keyof custom_field, value: any) => {
        const updatedFields: custom_field[] = [...custom_fields.value];

        updatedFields[idx] = {
            ...updatedFields[idx],
            [subKey]: value,
        };

        custom_fields.onChange(updatedFields);

        dispatchCartConfig({
            isComplex: true,
            key: "additional_product",
            payload: {
                custom_fields: updatedFields,
            },
        });
    };

    return (
        <>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Additional Product Type</div>
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row1}`}>
                        {/* <div className={styles.cardLabel}>Settings</div> */}
                        <div
                            style={{
                                paddingInline: 16,
                            }}
                        >
                            <LegacyCard title="">
                                <Banner tone={"info"}>
                                    <div
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                            window.open(
                                                "https://www.loom.com/share/d86fe84f7eda4efc8a8a997f62ba0948",
                                                "_blank",
                                            )
                                        }
                                    >
                                        <p>
                                            This will create a new product in your store for this offer. Refer to the
                                            video if you’d like to hide it from collections and store search.
                                        </p>
                                    </div>
                                </Banner>
                            </LegacyCard>
                        </div>
                        <div className={styles.formRow}>
                            <Checkbox
                                label={"Shipping Protection / Gift Wrapping"}
                                checked={!!additional_product_shipping_gift_checkbox?.value} // new state name
                                onChange={(val: boolean) => {
                                    dispatchCartConfig({
                                        isComplex: true,
                                        payload: {
                                            additional_product_shipping_gift_checkbox: val,
                                        },
                                        key: "additional_product",
                                    });
                                    additional_product_shipping_gift_checkbox.onChange(val);
                                }}
                            />
                        </div>

                        {additional_product_shipping_gift_checkbox?.value == true && (
                            <div style={{ marginLeft: "20px" }}>
                                <div className={`${styles.formRow} ${styles.row2} `} style={{ marginBottom: "10px" }}>
                                    <RadioButton
                                        label={"Shipping Protection"}
                                        name={"shipping_protection"}
                                        {...asChoiceField(product_type, "shipping_protection")}
                                        onChange={() => {
                                            const val = "shipping_protection";
                                            dispatchCartConfig({
                                                isComplex: true,
                                                payload: {
                                                    product_type: val,
                                                },
                                                key: "additional_product",
                                            });
                                            product_type.onChange(val);
                                        }}
                                    />

                                    <div className={`${styles.formRow} ${styles.row1}`}>
                                        <RadioButton
                                            label={"Gift Wrapping"}
                                            name={"gift_wrapping"}
                                            {...asChoiceField(product_type, "gift_wrapping")}
                                            onChange={() => {
                                                const val = "gift_wrapping";
                                                dispatchCartConfig({
                                                    isComplex: true,
                                                    payload: {
                                                        product_type: val,
                                                    },
                                                    key: "additional_product",
                                                });
                                                product_type.onChange(val);
                                            }}
                                        />
                                    </div>
                                </div>

                                {product_type?.value == "shipping_protection" && (
                                    <div>
                                        <div className={styles.cardLabel}>Shipping Protection Settings</div>
                                        <FormLayout>
                                            <div className={styles.cardSubLabel}>Title</div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <TextEditorCart
                                                    placeholder="Title"
                                                    value={shipping_protection_title.value}
                                                    onChange={(value: any) => {
                                                        const cleanedValue = value === "<p><br></p>" ? "" : value;
                                                        shipping_protection_title.onChange(cleanedValue);
                                                        dispatchCartConfig({
                                                            payload: {
                                                                shipping_protection_title: cleanedValue,
                                                            },
                                                            isComplex: true,
                                                            key: "additional_product",
                                                        });
                                                    }}
                                                    submit={shipping_protection_title?.onBlur}
                                                    error={shipping_protection_title?.error}
                                                />
                                            </div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <TextField
                                                    label={"Description"}
                                                    placeholder={"Description"}
                                                    autoComplete={"off"}
                                                    helpText={"Use {price} for the pricing"}
                                                    {...shipping_protection_subtext}
                                                    onBlur={shipping_protection_subtext?.onBlur}
                                                    onChange={(val) => {
                                                        dispatchCartConfig({
                                                            payload: {
                                                                shipping_protection_subtext: val,
                                                            },
                                                            isComplex: true,
                                                            key: "additional_product",
                                                        });
                                                        shipping_protection_subtext.onChange(val);
                                                    }}
                                                    // maxLength={80}
                                                    showCharacterCount
                                                    error={shipping_protection_subtext.error}
                                                />
                                            </div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <TextField
                                                    label={"Price"}
                                                    placeholder={"00"}
                                                    value={shipping_protection_price?.value?.toString()}
                                                    type={"number"}
                                                    suffix={getNumberFieldSuffix("flat", money_format).suffix}
                                                    onChange={(value) => {
                                                        dispatchCartConfig({
                                                            payload: {
                                                                shipping_protection_price: +value,
                                                            },
                                                            isComplex: true,
                                                            key: "additional_product",
                                                        });
                                                        shipping_protection_price.onChange(+value);
                                                    }}
                                                    autoComplete="off"
                                                    error={shipping_protection_price.error}
                                                    onBlur={shipping_protection_price?.onBlur}
                                                />
                                            </div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <MediaGallery
                                                    dispatchBundleConfig={dispatchCartConfig as unknown as any}
                                                    images={shipping_protection_images}
                                                    updateKey={"shipping_protection_images"}
                                                    parentKey={"additional_product"}
                                                    maxFiles={1}
                                                />
                                            </div>
                                        </FormLayout>
                                    </div>
                                )}

                                {product_type?.value == "gift_wrapping" && (
                                    <div>
                                        <div className={styles.cardLabel}>Gift Wrapping Settings</div>
                                        <FormLayout>
                                            <div className={styles.cardSubLabel}>Title</div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <TextEditorCart
                                                    placeholder="Title"
                                                    value={gift_wrapping_title.value}
                                                    onChange={(value: any) => {
                                                        const cleanedValue = value === "<p><br></p>" ? "" : value;
                                                        gift_wrapping_title.onChange(cleanedValue);
                                                        dispatchCartConfig({
                                                            payload: {
                                                                gift_wrapping_title: cleanedValue,
                                                            },
                                                            isComplex: true,
                                                            key: "additional_product",
                                                        });
                                                    }}
                                                    error={gift_wrapping_title?.error}
                                                    submit={gift_wrapping_title?.onBlur}
                                                />
                                            </div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <TextField
                                                    label={"Description"}
                                                    placeholder={"Description"}
                                                    autoComplete={"off"}
                                                    helpText={"Use {price} for the pricing"}
                                                    {...gift_wrapping_subtext}
                                                    onBlur={gift_wrapping_subtext?.onBlur}
                                                    onChange={(val) => {
                                                        dispatchCartConfig({
                                                            payload: {
                                                                gift_wrapping_subtext: val,
                                                            },
                                                            isComplex: true,
                                                            key: "additional_product",
                                                        });
                                                        gift_wrapping_subtext.onChange(val);
                                                    }}
                                                    maxLength={80}
                                                    showCharacterCount
                                                    error={gift_wrapping_subtext.error}
                                                />
                                            </div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <TextField
                                                    label={"Price"}
                                                    placeholder={"00"}
                                                    value={gift_wrapping_price?.value?.toString()}
                                                    type={"number"}
                                                    min={0}
                                                    onChange={(value) => {
                                                        dispatchCartConfig({
                                                            payload: {
                                                                gift_wrapping_price: +value,
                                                            },
                                                            isComplex: true,
                                                            key: "additional_product",
                                                        });
                                                        gift_wrapping_price.onChange(+value);
                                                    }}
                                                    autoComplete="off"
                                                    error={gift_wrapping_price.error}
                                                    onBlur={gift_wrapping_price?.onBlur}
                                                />
                                            </div>
                                            <div className={`${styles.formRow} ${styles.row1}`}>
                                                <MediaGallery
                                                    dispatchBundleConfig={dispatchCartConfig as unknown as any}
                                                    images={gift_wrapping_images}
                                                    updateKey={"gift_wrapping_images"}
                                                    parentKey={"additional_product"}
                                                    maxFiles={1}
                                                />
                                            </div>
                                        </FormLayout>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </FormLayout>

                <div
                    className={styles.cardLabel}
                    style={{
                        marginTop: "10px",
                    }}
                >
                    Addon Product Type
                </div>
                <FormLayout>
                    <div className={`${styles.formRow} ${styles.row1}`}>
                        {/* <div className={styles.cardLabel}>Settings</div> */}

                        <div className={styles.formRow}>
                            <Checkbox
                                label={"Add Another Addon"}
                                checked={!!additional_product_addon_checkbox?.value}
                                onChange={(val: boolean) => {
                                    dispatchCartConfig({
                                        isComplex: true,
                                        payload: {
                                            additional_product_addon_checkbox: val,
                                        },
                                        key: "additional_product",
                                    });
                                    additional_product_addon_checkbox.onChange(val);
                                }}
                            />
                        </div>

                        {additional_product_addon_checkbox?.value == true && (
                            <div style={{ marginLeft: "20px" }}>
                                <div>
                                    <div className={styles.cardLabel}>Addon Settings</div>
                                    <FormLayout>
                                        <div className={styles.cardSubLabel}>Title</div>
                                        <div className={`${styles.formRow} ${styles.row1}`}>
                                            <TextEditorCart
                                                placeholder="Title"
                                                helpText={"Use {productPrice} for the pricing"}
                                                value={additional_addon_title.value}
                                                onChange={(value: any) => {
                                                    const cleanedValue = value === "<p><br></p>" ? "" : value;
                                                    additional_addon_title.onChange(cleanedValue);
                                                    dispatchCartConfig({
                                                        payload: {
                                                            additional_addon_title: cleanedValue,
                                                        },
                                                        isComplex: true,
                                                        key: "additional_product",
                                                    });
                                                }}
                                                submit={additional_addon_title?.onBlur}
                                                error={additional_addon_title?.error}
                                            />
                                        </div>
                                        <div className={`${styles.formRow} ${styles.row1}`}>
                                            <TextField
                                                label={"Description"}
                                                placeholder={"Description"}
                                                autoComplete={"off"}
                                                helpText={"Use {productPrice} for the pricing"}
                                                {...additional_addon_subtext}
                                                onBlur={additional_addon_subtext?.onBlur}
                                                onChange={(val) => {
                                                    dispatchCartConfig({
                                                        payload: {
                                                            additional_addon_subtext: val,
                                                        },
                                                        isComplex: true,
                                                        key: "additional_product",
                                                    });
                                                    additional_addon_subtext.onChange(val);
                                                }}
                                                maxLength={150}
                                                showCharacterCount
                                                error={additional_addon_subtext.error}
                                            />
                                        </div>

                                        <div className={styles.upsellProductsSelection}>
                                            <div className={styles.sectionLabel}>Addon Products</div>
                                            {additional_addon_products?.value.length ? (
                                                <div className={styles.upsellProducts}>
                                                    {additional_addon_products?.value.map(
                                                        (product: ProductObj, index: number) => (
                                                            <ProductRow
                                                                key={product.id + index}
                                                                product={product}
                                                                idx={index}
                                                                updateKey="additional_addon_products"
                                                                dispatchCartConfig={dispatchCartConfig}
                                                                products={additional_addon_products}
                                                            />
                                                        ),
                                                    )}
                                                    <div className={styles.addProductsSticky}>
                                                        <span className={styles.emptySpacer}></span>
                                                        <Button
                                                            variant={"secondary"}
                                                            size={"micro"}
                                                            onClick={() =>
                                                                handleProductSearch(
                                                                    "additional_addon_products",
                                                                    additional_addon_products?.value,
                                                                )
                                                            }
                                                        >
                                                            {"Add more Addon Products"}
                                                        </Button>
                                                        <span className={styles.emptySpacer}></span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    className={styles.noUpsells}
                                                    onClick={() =>
                                                        handleProductSearch(
                                                            "additional_addon_products",
                                                            additional_addon_products?.value,
                                                        )
                                                    }
                                                >
                                                    <Button variant={"secondary"} size={"micro"}>
                                                        {"Addon Products"}
                                                    </Button>
                                                </div>
                                            )}
                                            {!!additional_addon_products?.error && (
                                                <ShopifyNativeError
                                                    error={additional_addon_products.error}
                                                    style={{ marginTop: 5, paddingLeft: 2 }}
                                                />
                                            )}
                                        </div>

                                        <div className={`${styles.formRow} ${styles.row1}`}>
                                            <TextField
                                                label={"Addon Popup Title"}
                                                placeholder={"Gift option information"}
                                                autoComplete={"off"}
                                                {...addon_modal_title}
                                                onBlur={addon_modal_title?.onBlur}
                                                onChange={(val) => {
                                                    dispatchCartConfig({
                                                        payload: {
                                                            addon_modal_title: val,
                                                        },
                                                        isComplex: true,
                                                        key: "additional_product",
                                                    });
                                                    addon_modal_title.onChange(val);
                                                }}
                                                maxLength={100}
                                                showCharacterCount
                                                error={addon_modal_title.error}
                                            />
                                        </div>

                                        <div className={`${styles.formRow} ${styles.row1}`}>
                                            <TextField
                                                label={"Addon Selector Label"}
                                                placeholder={"Greeting Card"}
                                                autoComplete={"off"}
                                                {...addon_selector_label}
                                                onBlur={addon_selector_label?.onBlur}
                                                onChange={(val) => {
                                                    dispatchCartConfig({
                                                        payload: {
                                                            addon_selector_label: val,
                                                        },
                                                        isComplex: true,
                                                        key: "additional_product",
                                                    });
                                                    addon_selector_label.onChange(val);
                                                }}
                                                maxLength={100}
                                                showCharacterCount
                                                error={addon_selector_label.error}
                                            />
                                        </div>

                                        <Divider />
                                        <Checkbox
                                            label={"Custom Fields"}
                                            checked={!!custom_fields_checkbox?.value}
                                            onChange={(val: boolean) => {
                                                dispatchCartConfig({
                                                    isComplex: true,
                                                    payload: {
                                                        custom_fields_checkbox: val,
                                                    },
                                                    key: "additional_product",
                                                });
                                                custom_fields_checkbox.onChange(val);
                                            }}
                                        />

                                        {custom_fields_checkbox.value == true && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 10,
                                                    marginTop: "10px",
                                                }}
                                            >
                                                <FormProvider {...methods}>
                                                    {!!custom_fields?.value?.length &&
                                                        custom_fields.value.map((field: any, sIdx: number) => {
                                                            return (
                                                                <FieldRow
                                                                    handleEditFields={handleEditFields}
                                                                    field={field}
                                                                    sIdx={sIdx}
                                                                    pIdx={0}
                                                                    handleAddFields={handleAddFields}
                                                                    cartAddon={true}
                                                                />
                                                            );
                                                        })}
                                                </FormProvider>
                                                {custom_fields.value.length <= 4 && (
                                                    <Button onClick={() => handleAddFields(0, "add")} fullWidth>
                                                        Add Field
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </FormLayout>
                                </div>
                            </div>
                        )}
                    </div>
                </FormLayout>
            </div>

            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Color</div>
                <FormLayout>
                    <div className={`${styles.formRow} ${!showCartPreview && styles.row3}`}>
                        <NewCustomColorPicker
                            hexColor={additional_product_card_bg.value}
                            setHexColor={(val) => {
                                additional_product_card_bg.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        additional_product_card_bg: val,
                                    },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Card Background Color"}
                            props={{
                                ...additional_product_card_bg,
                                onBlur: () => additional_product_card_bg?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={additional_product_title_color.value}
                            setHexColor={(val) => {
                                additional_product_title_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        additional_product_title_color: val,
                                    },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Title Color"}
                            props={{
                                ...additional_product_title_color,
                                onBlur: () => additional_product_title_color?.runValidation(),
                            }}
                        />
                        <NewCustomColorPicker
                            hexColor={additional_product_description_color.value}
                            setHexColor={(val) => {
                                additional_product_description_color.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        additional_product_description_color: val,
                                    },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Description Color"}
                            props={{
                                ...additional_product_description_color,
                                onBlur: () => additional_product_description_color?.runValidation(),
                            }}
                        />

                        <NewCustomColorPicker
                            hexColor={addon_modal_background_color.value}
                            setHexColor={(val) => {
                                addon_modal_background_color.onChange(val);
                                dispatchCartConfig({
                                    payload: { addon_modal_background_color: val },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Modal Background Color"}
                            props={{
                                ...addon_modal_background_color,
                                onBlur: () => addon_modal_background_color?.runValidation(),
                            }}
                        />

                        <NewCustomColorPicker
                            hexColor={addon_modal_title_color.value}
                            setHexColor={(val) => {
                                addon_modal_title_color.onChange(val);
                                dispatchCartConfig({
                                    payload: { addon_modal_title_color: val },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Modal Title Color"}
                            props={{
                                ...addon_modal_title_color,
                                onBlur: () => addon_modal_title_color?.runValidation(),
                            }}
                        />

                        <NewCustomColorPicker
                            hexColor={addon_modal_primary_button_bg.value}
                            setHexColor={(val) => {
                                addon_modal_primary_button_bg.onChange(val);
                                dispatchCartConfig({
                                    payload: { addon_modal_primary_button_bg: val },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Primary Button Background"}
                            props={{
                                ...addon_modal_primary_button_bg,
                                onBlur: () => addon_modal_primary_button_bg?.runValidation(),
                            }}
                        />

                        <NewCustomColorPicker
                            hexColor={addon_modal_primary_button_text_color.value}
                            setHexColor={(val) => {
                                addon_modal_primary_button_text_color.onChange(val);
                                dispatchCartConfig({
                                    payload: { addon_modal_primary_button_text_color: val },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Primary Button Text Color"}
                            props={{
                                ...addon_modal_primary_button_text_color,
                                onBlur: () => addon_modal_primary_button_text_color?.runValidation(),
                            }}
                        />

                        <NewCustomColorPicker
                            hexColor={addon_modal_secondary_button_text_color.value}
                            setHexColor={(val) => {
                                addon_modal_secondary_button_text_color.onChange(val);
                                dispatchCartConfig({
                                    payload: { addon_modal_secondary_button_text_color: val },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            label={"Secondary Button Text Color"}
                            props={{
                                ...addon_modal_secondary_button_text_color,
                                onBlur: () => addon_modal_secondary_button_text_color?.runValidation(),
                            }}
                        />
                    </div>
                    {selected_drawer_template.value == AvailableCartDrawerTemplates.MODERN && (
                        <>
                            <div className={styles.cardSubLabel} style={{ marginBottom: 0 }}>
                                Card Border
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    gap: 15,
                                    marginBottom: 10,
                                    marginTop: 5,
                                }}
                            >
                                <RadioButton
                                    label={"Solid"}
                                    checked={!additional_product_border_gradient_status.value}
                                    onChange={() => {
                                        additional_product_border_gradient_status.onChange(false);
                                        dispatchCartConfig({
                                            payload: {
                                                additional_product_border_gradient_status: false,
                                            },
                                            isComplex: true,
                                            key: "additional_product_design",
                                        });
                                    }}
                                />
                                <RadioButton
                                    label={"Gradient"}
                                    checked={additional_product_border_gradient_status.value}
                                    onChange={() => {
                                        additional_product_border_gradient_status.onChange(true);
                                        dispatchCartConfig({
                                            payload: {
                                                additional_product_border_gradient_status: true,
                                            },
                                            isComplex: true,
                                            key: "additional_product_design",
                                        });
                                    }}
                                />
                            </div>
                            <div className={`${styles.formRow} ${!showCartPreview && styles.row3}`}>
                                <NewCustomColorPicker
                                    hexColor={additional_product_border_color.value}
                                    setHexColor={(val) => {
                                        additional_product_border_color.onChange(val);
                                        dispatchCartConfig({
                                            payload: {
                                                additional_product_border_color: val,
                                            },
                                            isComplex: true,
                                            key: "additional_product_design",
                                        });
                                    }}
                                    label={"Border Color"}
                                    props={{
                                        ...additional_product_border_color,
                                        onBlur: () => additional_product_border_color?.runValidation(),
                                    }}
                                />
                                {additional_product_border_gradient_status.value && (
                                    <>
                                        <NewCustomColorPicker
                                            hexColor={additional_product_border_gradient_color.value}
                                            setHexColor={(val) => {
                                                additional_product_border_gradient_color.onChange(val);
                                                dispatchCartConfig({
                                                    payload: {
                                                        additional_product_border_gradient_color: val,
                                                    },
                                                    isComplex: true,
                                                    key: "additional_product_design",
                                                });
                                            }}
                                            label={"Border Color Gradient"}
                                            props={{
                                                ...additional_product_border_gradient_color,
                                                onBlur: () => additional_product_border_gradient_color?.runValidation(),
                                            }}
                                        />
                                        <RangeSlider
                                            min={0}
                                            max={360}
                                            label={"Gradient Direction"}
                                            value={additional_product_border_gradient_direction.value}
                                            onChange={(val) => {
                                                additional_product_border_gradient_direction.onChange(val);
                                                dispatchCartConfig({
                                                    payload: {
                                                        additional_product_border_gradient_direction: val,
                                                    },
                                                    isComplex: true,
                                                    key: "additional_product_design",
                                                });
                                            }}
                                            suffix={(additional_product_border_gradient_direction.value || 0) + "px"}
                                            output
                                            error={additional_product_border_gradient_direction.error}
                                            disabled={!additional_product_border_gradient_status?.value}
                                        />
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </FormLayout>
            </div>

            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Typography</div>
                <FormLayout>
                    <div className={`${styles.formRow} ${!showCartPreview && styles.row3}`}>
                        <TextField
                            label={"Title Text Size"}
                            placeholder={"13"}
                            value={additional_product_title_size?.value?.toString()}
                            type={"number"}
                            onChange={(value) => {
                                dispatchCartConfig({
                                    payload: {
                                        additional_product_title_size: +value,
                                    },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                                additional_product_title_size.onChange(+value);
                            }}
                            min={0}
                            autoComplete="off"
                            error={additional_product_title_size.error}
                            onBlur={additional_product_title_size?.onBlur}
                        />
                        <TextField
                            label={"Description Text Size"}
                            placeholder={"13"}
                            value={additional_product_description_size?.value?.toString()}
                            type={"number"}
                            onChange={(value) => {
                                dispatchCartConfig({
                                    payload: {
                                        additional_product_description_size: +value,
                                    },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                                additional_product_description_size.onChange(+value);
                            }}
                            min={0}
                            autoComplete="off"
                            error={additional_product_description_size.error}
                            onBlur={additional_product_description_size?.onBlur}
                        />

                        <TextField
                            label={"Addon Popup Text Size"}
                            placeholder={"100"}
                            value={addon_modal_title_size?.value?.toString()}
                            type={"number"}
                            onChange={(value) => {
                                dispatchCartConfig({
                                    payload: {
                                        addon_modal_title_size: +value,
                                    },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                                addon_modal_title_size.onChange(+value);
                            }}
                            min={0}
                            autoComplete="off"
                            error={addon_modal_title_size?.error}
                            onBlur={addon_modal_title_size?.onBlur}
                        />
                        <Select
                            label={"Price Text Style"}
                            options={FontStyles}
                            placeholder={"Font style"}
                            onChange={(val) => {
                                additional_product_description_style.onChange(val);
                                dispatchCartConfig({
                                    payload: {
                                        additional_product_description_style: val,
                                    },
                                    isComplex: true,
                                    key: "additional_product_design",
                                });
                            }}
                            value={additional_product_description_style.value}
                        />
                    </div>
                </FormLayout>
            </div>
        </>
    );
};

export default CartAdditionalProductForm;
